import React from 'react'
import { Box, Typography, useTheme, styled } from '@mui/material'
import { DeleteOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { TopBarPreviewCard, DeleteButton, TopBarLockedBadge } from './DesignerCanvasSection.styles'
import { ITopBarComponentInstance, textAlignmentToCss } from 'src/types/report-designer'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

interface ITopBarTypographyProps {
  fontSize?: string
  color?: string
  fontWeight?: number
  textAlign?: string
  lineHeight?: number
  marginTop?: number
  fontFamily?: string
  letterSpacingPx?: number
}

export interface ICanvasTopBarItemProps {
  item: ITopBarComponentInstance
  isSelected: boolean
  accentColor: string
  onSelectItem: (id: string) => void
  onDeleteItem: (id: string) => void
}

export const CanvasTopBarItem: React.FC<ICanvasTopBarItemProps> = ({
  item,
  isSelected,
  accentColor,
  onSelectItem,
  onDeleteItem,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const theme = useTheme()

  return (
    <TopBarPreviewCard
      key={item.id}
      isSelected={isSelected}
      accentColor={accentColor}
      bgColor={item.backgroundColor ?? '#1a1a2e'}
      onClick={() => onSelectItem(item.id)}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TopBarTypography
          fontSize={item.titleStyles?.fontSize ? `${item.titleStyles.fontSize}px` : '1rem'}
          color={item.titleStyles?.fontColor ?? '#ffffff'}
          fontWeight={item.titleStyles?.bold ? 700 : 400}
          fontFamily={item.titleStyles?.fontFamily ?? 'arial'}
          textAlign={textAlignmentToCss(item.titleStyles?.alignment)}
          lineHeight={item.titleStyles?.lineHeight ?? 1.25}
          letterSpacingPx={item.titleStyles?.letterSpacing ?? 0}
        >
          {item.title || translate('top-bar-title-placeholder', { ns: 'dynamic-report' })}
        </TopBarTypography>
        <TopBarTypography
          fontSize={item.subtitleStyles?.fontSize ? `${item.subtitleStyles.fontSize}px` : '0.75rem'}
          color={item.subtitleStyles?.fontColor ?? '#aaaaaa'}
          fontWeight={item.subtitleStyles?.bold ? 700 : 400}
          fontFamily={item.subtitleStyles?.fontFamily ?? 'arial'}
          textAlign={textAlignmentToCss(item.subtitleStyles?.alignment)}
          lineHeight={item.subtitleStyles?.lineHeight ?? 1.3}
          marginTop={0.25}
          letterSpacingPx={item.subtitleStyles?.letterSpacing ?? 0}
        >
          {item.subtitle || translate('top-bar-subtitle-placeholder', { ns: 'dynamic-report' })}
        </TopBarTypography>
      </Box>
      <TopBarLockedBadge>{translate('top-bar-locked', { ns: 'dynamic-report' })}</TopBarLockedBadge>
      <DeleteButton
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          onDeleteItem(item.id)
        }}
        title={translate('delete-item', { ns: 'dynamic-report' })}
        sx={{
          color: getThemeColor(theme, MUIThemeColors.secondaryDark),
          '&:hover': {
            color: getThemeColor(theme, MUIThemeColors.errorDark),
            backgroundColor: getThemeColor(theme, MUIThemeColors.primaryDark),
          },
        }}
      >
        <DeleteOutlined fontSize="small" />
      </DeleteButton>
    </TopBarPreviewCard>
  )
}

const TopBarTypography = styled(Typography, {
  shouldForwardProp: prop =>
    ![
      'fontSize',
      'color',
      'fontWeight',
      'textAlign',
      'lineHeight',
      'marginTop',
      'fontFamily',
      'letterSpacingPx',
    ].includes(prop as string),
})<ITopBarTypographyProps>(
  ({ fontSize, color, fontWeight, textAlign, lineHeight, marginTop, fontFamily, letterSpacingPx }) =>
    ({
      fontSize: fontSize as any,
      color: color as any,
      fontWeight: fontWeight as any,
      fontFamily: fontFamily as any,
      textAlign: textAlign as any,
      lineHeight: lineHeight as any,
      marginTop: marginTop as any,
      letterSpacing: letterSpacingPx ? `${letterSpacingPx}px` : 'normal',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as any),
)
